import React, { useContext, useState, useEffect, useRef } from 'react';

import InputMask from 'react-input-mask';
import { useLocation, useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

import { renderIconForCard } from './PaymentInstrument';

import PaymentCardTooltip from '../../../assets/images/payment-card-tooltip.svg';
import { UserContext } from '../../../context/UserContext';
import { useLoanDetails } from '../../../hooks/useLoanDetails';
import { createPaymentInstrument, switchPaymentInstrumentForLoan } from '../../../service/loan';
import BnplLoading from '../BnplLoading';

export const RenderCardInfo = ({ isSectionDelete = false, cardInfo }) => {
  const { setLoading } = useContext(UserContext);
  const [data, setData] = useState({ isSubmitting: false });
  const [dataError, setDataError] = useState({});
  const [collectJsLoader, setCollectJsLoader] = useState(true);
  const [collectJsInstance, setCollectJsInstance] = useState({});
  const navigate = useNavigate();
  const { loan } = useLoanDetails();
  const { id: loanId } = useParams();
  const ref = useRef(null);
  const disableAuthorise =
    Object.values(dataError).length === 0 || Object.values(dataError).some((el) => el === true);
  localStorage.setItem('loan.payeeId', loan?.payeeId);

  useEffect(() => {
    const interval = setTimeout(() => {
      if (!Object.keys(collectJsInstance).length) {
        window.location.reload();
      }
    }, 50);
    return () => clearTimeout(interval);
  }, [collectJsInstance]);

  useEffect(() => {
    if (window.CollectJS) {
      setCollectJsInstance(window.CollectJS);
      window.CollectJS.configure({
        variant: 'inline',
        styleSniffer: true,
        validationCallback: (field, valid) => {
          if (valid) {
            setDataError((dataError) => ({
              ...dataError,
              [field]: false,
            }));
          } else {
            setDataError((dataError) => ({
              ...dataError,
              [field]: true,
            }));
          }
        },
        callback: (token) => {
          setData({
            ...data,
            isSubmitting: false,
          });
          finishSubmit(token);
        },
        fieldsAvailableCallback: () => {
          setCollectJsLoader(false);
        },
        googleFont: 'Lato:400',
        invalidCss: {
          color: '#727272',
          'background-color': 'rgba(243, 106, 63, 0.05)',
          border: '1px solid #F36A3F',
        },
        validCss: {
          color: '#727272',
          'background-color': '#fff',
        },
        placeholderCss: {
          color: '#727272',
          'background-color': 'transparent',
        },
        focusCss: {
          color: '#727272',
          'background-color': '#fff',
        },
        customCss: {
          'margin-bottom': '20px',
          'font-weight': '600',
          'font-size': '27px',
          height: '56px',
        },
        fields: {
          ccnumber: {
            title: 'Card Number',
            selector: '#ccnumber',
            placeholder: '____ ____ ____ ____',
          },
          ccexp: {
            title: 'Exp. Date',
            selector: '#ccexp',
            placeholder: '__ / __',
          },
          cvv: {
            title: 'CVV2',
            selector: '#cvv',
            placeholder: '___',
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (event) => {
    setLoading(true);
    event.preventDefault();
    setData({ ...data, isSubmitting: true });
    window.CollectJS.startPaymentRequest();
  };

  const finishSubmit = async (response) => {
    const { isSubmitting, ...formData } = { ...data };
    formData.token = response.token;
    setData({
      ...data,
      isSubmitting: false,
    });

    if (response.token) {
      try {
        const payeeId = localStorage.getItem('loan.payeeId');
        const financialInstrument = {
          itemType: 'TokenizedPaymentProperty',
          dataType: 'tokenizedPayment',
          name: ref.current.value,
          value: {
            token: response.token,
            forceDebitCard: false,
            dryRunMode: false,
            binNumber: response.card.bin,
            paymentType: response.card.type,
            maskedPaymentIdentifier: response.card.number.slice(-4),
          },
        };
        const { data } = await createPaymentInstrument(financialInstrument, payeeId);
        const preparingData = {
          autoPayEnabled: false,
          payeeId,
          autoPayInstrumentId: data?.instrumentId,
        };
        await switchPaymentInstrumentForLoan(preparingData, loanId);
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
        localStorage.removeItem('loan.payeeId');
        navigate(-1);
      }
    }
  };

  const renderCardNumber = (isSectionDelete) => {
    return (
      <label htmlFor="card-number">
        <div className="card-number-with-icon">
          {isSectionDelete ? renderIconForCard(cardInfo?.type) : null}
          <span>Card Number</span>
        </div>
        {isSectionDelete ? (
          <InputMask
            mask="9999-9999-9999-9999"
            readOnly
            value={cardInfo?.number}
            name="card-number"
            spellCheck={false}
          />
        ) : (
          <div id="ccnumber" />
        )}
      </label>
    );
  };

  const renderExpDateAndCvv = (isSectionDelete) => {
    return (
      <div className="private-input-wrapper">
        <label htmlFor="card-expiration-date">
          Exp. Date
          <span className="form-icon" data-tip="Enter your expiration date">
            <img src={PaymentCardTooltip} alt="Enter your exp. data" />
          </span>
          {isSectionDelete ? (
            <InputMask
              mask="99/99"
              readOnly
              value={cardInfo?.expDate}
              name="card-expiration-date"
              spellCheck={false}
            />
          ) : (
            <div id="ccexp" />
          )}
        </label>
        <label htmlFor="card-CVV">
          CVV2
          <span className="form-icon" data-tip="Enter your CVV">
            <img src={PaymentCardTooltip} alt="Enter your CVV" />
          </span>
          {isSectionDelete ? (
            <InputMask
              mask="999"
              maskChar=""
              readOnly
              value={cardInfo?.cvv}
              name="card-CVV"
              type="password"
              spellCheck={false}
            />
          ) : (
            <div id="cvv" />
          )}
        </label>
        <ReactTooltip
          className="tooltip"
          place="left"
          textColor="#fcfcfaff"
          backgroundColor="#423176ff"
          effect="solid"
          html={true}
        />
      </div>
    );
  };
  const goBack = () => {
    const prevPath = localStorage.getItem('prevPath');
    if (prevPath) {
      navigate(prevPath);
    } else {
      navigate(`/account/${loanId}`);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <label htmlFor="cardholder-name">
          Cardholder Name
          <input ref={ref} name="cardholder-name" type="text" spellCheck={false} />
        </label>
        <div className="render-card-wrapper">
          {renderCardNumber(isSectionDelete)}
          {renderExpDateAndCvv(isSectionDelete)}
          <div className="add-card-button-wrapper">
            <a onClick={goBack}>Go Back</a>
            <button
              className="btn secondary-btn"
              disabled={data.isSubmitting || disableAuthorise}
              type="submit"
            >{`${isSectionDelete ? 'Delete' : 'Add'}`}</button>
          </div>
        </div>
      </form>
      {collectJsLoader && (
        <div className={'collect-js-loading-wrapper'}>
          <BnplLoading className={'bnpl-form'} isAuth />
        </div>
      )}
    </>
  );
};
